import { useI18n } from "@baton/i18n";
import { AlertDialog as Primitive } from "radix-ui";
import { useId, useState, type ReactNode } from "react";
import { cn } from "../../cn.ts";
import { Button, type ButtonVariant } from "../../primitives/buttons/Button/Button.tsx";
import { Input } from "../../primitives/inputs/Input/Input.tsx";
import { useOverlayStage } from "../portalContainer.tsx";
import { useReturnFocus } from "../returnFocus.ts";
import { dialogDescriptionClassName, dialogTitleClassName, scrimClassName } from "../surfaces.ts";

/**
 * FR-015-101, § Overlays, AlertDialog, taken from shadcn/ui's Alert Dialog over Radix (Spectrum UI
 * has none) and themed through the variables of `packages/tokens` (2026-09-15, by the owner's
 * decision): as Dialog, with the confirm button disabled until `requireTyped` matches. Also the
 * dialog behind `HoldToConfirm` in § Spectrum UI sources.
 *
 * What differs from the source, and why:
 * - the panel is the small dialog's width (`--dialog-width-sm`) inside a `--space-6` margin, where
 *   the source writes 32 rem and a 2 rem margin as raw lengths;
 * - the confirm button is Baton's Button outside Radix's `Action`, because an asynchronous confirm
 *   stays open with its spinner until the work settles, where `Action` closes on click; Cancel is
 *   Radix's `Cancel` as in the source;
 * - the media slot is not carried, because no Baton confirmation shows an illustration.
 */
export interface AlertDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: ReactNode;
  readonly body: ReactNode;
  readonly confirmLabel: string;
  readonly cancelLabel?: string;
  readonly confirmVariant?: ButtonVariant;
  readonly onConfirm: () => void | Promise<void>;
  /** A string the user must type before confirm enables. */
  readonly requireTyped?: string;
  readonly trigger?: ReactNode;
  /**
   * Where the focus goes as the dialog closes, asked at that moment: a confirm that removes the
   * control which opened the dialog (a row's menu, a ticket's Withdraw) names the element that stays,
   * and null leaves the focus to go back to the opener (2026-10-21, found by 028's keyboard pass).
   */
  readonly closeFocus?: () => HTMLElement | null;
  readonly className?: string;
}

export function AlertDialog({ open, onOpenChange, title, body, confirmLabel, cancelLabel, confirmVariant = "primary", onConfirm, requireTyped, trigger, closeFocus, className }: AlertDialogProps) {
  const { t } = useI18n();
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const typedId = useId();
  const ready = requireTyped === undefined || typed === requireTyped;
  const overlayStage = useOverlayStage();
  useReturnFocus(open);
  return (
    <Primitive.Root
      data-slot="alert-dialog"
      open={open}
      onOpenChange={(next) => {
        if (!next) setTyped("");
        onOpenChange(next);
      }}
    >
      {trigger !== undefined && (
        <Primitive.Trigger data-slot="alert-dialog-trigger" asChild>
          {trigger}
        </Primitive.Trigger>
      )}
      <Primitive.Portal data-slot="alert-dialog-portal" {...(overlayStage === undefined ? {} : { container: overlayStage })}>
        <Primitive.Overlay data-slot="alert-dialog-overlay" className={scrimClassName} />
        <Primitive.Content
          data-slot="alert-dialog-content"
          data-size="sm"
          onCloseAutoFocus={(event) => {
            const target = closeFocus?.() ?? null;
            if (target === null) return;
            event.preventDefault();
            target.focus();
          }}
          className={cn(
            "fixed top-1/2 left-1/2 z-(--layer-overlay) grid w-full max-w-[calc(100%_-_var(--space-6)_*_2)] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-lg border bg-background p-6 shadow-lg duration-(--duration-slow) data-[state=closed]:animate-out data-[state=closed]:duration-(--duration-base) data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 sm:max-w-(--dialog-width-sm)",
            className,
          )}
        >
          <div data-slot="alert-dialog-header" className="grid place-items-center gap-1.5 text-center sm:place-items-start sm:text-start">
            <Primitive.Title data-slot="alert-dialog-title" className={dialogTitleClassName}>
              {title}
            </Primitive.Title>
            <Primitive.Description data-slot="alert-dialog-description" className={dialogDescriptionClassName}>
              {body}
            </Primitive.Description>
          </div>
          {requireTyped !== undefined && <Input id={typedId} label={t("ui.alert.typeToConfirm", { text: requireTyped })} value={typed} onChange={(event) => { setTyped(event.currentTarget.value); }} autoComplete="off" />}
          <div data-slot="alert-dialog-footer" className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Primitive.Cancel data-slot="alert-dialog-cancel" asChild>
              <Button variant="secondary">{cancelLabel ?? t("ui.alert.cancel")}</Button>
            </Primitive.Cancel>
            <Button
              variant={confirmVariant}
              disabled={!ready}
              loading={busy}
              onClick={() => {
                const result = onConfirm();
                if (result instanceof Promise) {
                  setBusy(true);
                  void result.finally(() => {
                    setBusy(false);
                  });
                }
              }}
            >
              {confirmLabel}
            </Button>
          </div>
        </Primitive.Content>
      </Primitive.Portal>
    </Primitive.Root>
  );
}
